"use client"

import { TrendingUp, TrendingDown } from "lucide-react"
import { useEffect, useState } from "react"
import { AnimatedList } from "@/components/ui/animated-list"
import { useTokenIcons } from "@/hooks/use-token-icons"

type Signal = {
  symbol: string
  direction: "LONG" | "SHORT"
  timeframe: string
  score: number
  time: string
  algoOk: boolean
}

type LiveSignal = Signal & {
  price: string
  change: string
  positive: boolean
}

const SIGNALS: Signal[] = [
  { symbol: "BTC",  direction: "LONG",  timeframe: "4h", score: 91, time: "2m ago",  algoOk: true  },
  { symbol: "SOL",  direction: "LONG",  timeframe: "1h", score: 87, time: "5m ago",  algoOk: true  },
  { symbol: "ETH",  direction: "SHORT", timeframe: "4h", score: 84, time: "9m ago",  algoOk: true  },
  { symbol: "AVAX", direction: "LONG",  timeframe: "1h", score: 82, time: "14m ago", algoOk: true  },
  { symbol: "BNB",  direction: "SHORT", timeframe: "1d", score: 79, time: "21m ago", algoOk: false },
  { symbol: "LINK", direction: "LONG",  timeframe: "4h", score: 77, time: "28m ago", algoOk: true  },
  { symbol: "SUI",  direction: "LONG",  timeframe: "1h", score: 76, time: "34m ago", algoOk: true  },
  { symbol: "DOT",  direction: "SHORT", timeframe: "4h", score: 74, time: "41m ago", algoOk: false },
  { symbol: "NEAR", direction: "LONG",  timeframe: "1h", score: 72, time: "47m ago", algoOk: true  },
  { symbol: "INJ",  direction: "LONG",  timeframe: "4h", score: 70, time: "53m ago", algoOk: true  },
  { symbol: "ARB",  direction: "SHORT", timeframe: "1h", score: 68, time: "1h ago",  algoOk: false },
  { symbol: "OP",   direction: "LONG",  timeframe: "4h", score: 66, time: "1h ago",  algoOk: true  },
]

function formatPrice(price: number): string {
  if (price >= 1000) return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  if (price >= 1) return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  return `$${price.toFixed(4)}`
}

function useLiveSignals(): LiveSignal[] {
  const [liveSignals, setLiveSignals] = useState<LiveSignal[]>(
    SIGNALS.map(s => ({ ...s, price: '...', change: '...', positive: true }))
  )

  useEffect(() => {
    const symbols = SIGNALS.map(s => `${s.symbol}USDT`)

    const fetchPrices = async () => {
      try {
        const res = await fetch(
          `https://fapi.binance.com/fapi/v1/ticker/24hr?symbols=${JSON.stringify(symbols)}`
        )
        const data: { symbol: string; lastPrice: string; priceChangePercent: string }[] = await res.json()
        const map: Record<string, { price: string; change: string; positive: boolean }> = {}
        for (const d of data) {
          const base = d.symbol.replace('USDT', '')
          const pct = parseFloat(d.priceChangePercent)
          const price = parseFloat(d.lastPrice)
          map[base] = {
            price: formatPrice(price),
            change: `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`,
            positive: pct >= 0,
          }
        }
        setLiveSignals(SIGNALS.map(s => ({
          ...s,
          price: map[s.symbol]?.price ?? '—',
          change: map[s.symbol]?.change ?? '—',
          positive: map[s.symbol]?.positive ?? true,
        })))
      } catch {
        // keep placeholder values on error
      }
    }

    fetchPrices()
    const interval = setInterval(fetchPrices, 15000)
    return () => clearInterval(interval)
  }, [])

  return liveSignals
}

const AVATAR_COLORS: Record<string, string> = {
  BTC:  "bg-orange-500/15 text-orange-400",
  ETH:  "bg-blue-500/15 text-blue-400",
  SOL:  "bg-purple-500/15 text-purple-400",
  BNB:  "bg-amber-500/15 text-amber-400",
  AVAX: "bg-red-500/15 text-red-400",
  LINK: "bg-blue-600/15 text-blue-300",
  SUI:  "bg-cyan-500/15 text-cyan-400",
  DOT:  "bg-pink-500/15 text-pink-400",
  NEAR: "bg-teal-500/15 text-teal-400",
  INJ:  "bg-indigo-500/15 text-indigo-400",
  ARB:  "bg-sky-500/15 text-sky-400",
  OP:   "bg-rose-500/15 text-rose-400",
}

function SignalCard({ signal, getIcon }: { signal: LiveSignal; getIcon: (s: string) => string | null }) {
  const avatarColor = AVATAR_COLORS[signal.symbol] ?? "bg-white/10 text-white/50"
  const iconUrl = getIcon(signal.symbol)
  const isLong = signal.direction === "LONG"

  return (
    <div className="w-full max-w-[340px] flex items-center gap-3 border border-white/10 bg-black px-4 py-3 shadow-xl shadow-black/50">
      {/* Avatar */}
      <div className={`relative h-8 w-8 shrink-0 flex items-center justify-center text-[10px] font-bold overflow-hidden ${avatarColor}`}>
        <span className="select-none">{signal.symbol.slice(0, 2)}</span>
        {iconUrl && (
          <img
            src={iconUrl}
            alt={signal.symbol}
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none" }}
          />
        )}
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-white">{signal.symbol}</span>
          {isLong
            ? <TrendingUp className="h-3 w-3 text-emerald-500 shrink-0" />
            : <TrendingDown className="h-3 w-3 text-red-500 shrink-0" />
          }
          <span className="text-[10px] text-white/30">{signal.timeframe}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-white/40">{signal.price}</span>
          <span className={`font-mono text-[10px] ${signal.positive ? "text-emerald-500" : "text-red-500"}`}>
            {signal.change}
          </span>
        </div>
      </div>

      {/* Right side */}
      <div className="flex flex-col items-end gap-1 shrink-0">
        <div className="flex items-center gap-1.5">
          {signal.algoOk ? (
            <span className="text-[8px] font-bold px-1 py-0.5 leading-none text-emerald-500 bg-emerald-500/10">
              ALGO OK
            </span>
          ) : (
            <span className="text-[8px] font-bold px-1 py-0.5 leading-none text-red-500 bg-red-500/10">
              REJECT
            </span>
          )}
          <span
            className="text-[9px] font-medium px-1.5 py-0.5 leading-none"
            style={{
              background: isLong ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
              color: isLong ? "#4ade80" : "#f87171",
            }}
          >
            {signal.direction}
          </span>
        </div>
        <span className="text-[9px] text-white/25 tabular-nums">{signal.time}</span>
      </div>
    </div>
  )
}

export function SignalsFeedSection() {
  const { getIcon } = useTokenIcons()
  const liveSignals = useLiveSignals()

  return (
    <section
      className="bg-black py-16 md:py-32"
      style={{ fontFamily: 'var(--font-ppmori, PPMori), sans-serif' }}
    >
      <div className="mx-auto max-w-5xl px-6">
        <div className="flex flex-col gap-12">

          {/* Left: text */}
          <div className="space-y-4">
            <h2 className="text-4xl font-medium text-white lg:text-5xl">
              100+ signals, every single day.
            </h2>
            <p className="text-white/50 text-lg">
              Algoglide pulls directly from Binance perpetual futures feeds — scanning 400+ pairs around the clock. Every candidate gets filtered by Algo before it reaches your feed, so you only see setups worth acting on.
            </p>
            <p className="text-white/50">
              We also mine <span className="text-white font-medium">Binance Square</span> — indexing signal group profiles and trader posts so you get curated signal feeds from the best voices in the market, without paying for a single Telegram or WhatsApp subscription.
            </p>
          </div>

          {/* Animated list — overflow-x visible, y masked */}
          <div
            className="relative h-[500px] w-full overflow-x-visible overflow-y-hidden"
            style={{
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)',
            }}
          >
            <AnimatedList
              stackGap={18}
              columnGap={72}
              scaleFactor={0.04}
              scrollDownDuration={6}
              formationDuration={1}
            >
              {liveSignals.map((signal) => (
                <SignalCard key={signal.symbol + signal.time} signal={signal} getIcon={getIcon} />
              ))}
            </AnimatedList>
          </div>

        </div>
      </div>
    </section>
  )
}
